import { useState, useEffect } from "react";
import { resolveItems } from "@/lib/api";
import { Shield, Swords, Heart, Zap, Star, Eye } from "lucide-react";
import { ilvlColor } from "@/lib/gearScore";

const WOWHEAD_BASE = "https://www.wowhead.com/tbc";

function refreshWowheadTooltips() {
  try { if (window.$WowheadPower?.refreshLinks) window.$WowheadPower.refreshLinks(); } catch {}
}

const LEFT_SLOTS = ["Head", "Shoulders", "Back", "Chest", "Wrists", "Hands", "Waist", "Legs", "Feet"];
const RIGHT_SLOTS = ["Neck", "Main Hand", "Off Hand", "Ranged", "Finger 1", "Finger 2", "Trinket 1", "Trinket 2"];

const CLASS_COLORS = {
  Warrior: "#C79C6E", Paladin: "#F58CBA", Hunter: "#ABD473", Rogue: "#FFF569",
  Priest: "#FFFFFF", Shaman: "#0070DE", Mage: "#69CCF0", Warlock: "#9482C9", Druid: "#FF7D0A",
};

const BASIC_STATS = [
  { key: "strength", label: "Strength", color: "#C79C6E" },
  { key: "agility", label: "Agility", color: "#ABD473" },
  { key: "stamina", label: "Stamina", color: "#FF7D0A" },
  { key: "intellect", label: "Intellect", color: "#69CCF0" },
  { key: "spirit", label: "Spirit", color: "#FFFFFF" },
];

const COMBAT_STATS = [
  { key: "armor", label: "Armor", color: "#fbbf24" },
  { key: "hit_melee", label: "Hit Rating", color: "#ef4444" },
  { key: "hit_spell", label: "Spell Hit", color: "#a78bfa" },
  { key: "crit_melee", label: "Crit Rating", color: "#ef4444" },
  { key: "crit_spell", label: "Spell Crit", color: "#a78bfa" },
  { key: "hit_ranged", label: "Ranged Hit", color: "#ABD473" },
  { key: "crit_ranged", label: "Ranged Crit", color: "#ABD473" },
  { key: "mp5", label: "MP5", color: "#3b82f6" },
];

const DEFENSE_STATS = [
  { key: "dodge", label: "Dodge", color: "#22c55e" },
  { key: "parry", label: "Parry", color: "#22c55e" },
  { key: "block", label: "Block", color: "#22c55e" },
  { key: "fire_res", label: "Fire Res", color: "#ef4444" },
  { key: "nature_res", label: "Nature Res", color: "#22c55e" },
  { key: "frost_res", label: "Frost Res", color: "#69CCF0" },
  { key: "shadow_res", label: "Shadow Res", color: "#9482C9" },
  { key: "arcane_res", label: "Arcane Res", color: "#fbbf24" },
];

export default function CharacterPanel({ data, wowClass }) {
  const gear = data?.gear || [];
  const stats = data?.character_stats || {};
  const [resolved, setResolved] = useState({});
  const [tab, setTab] = useState("gear");

  useEffect(() => {
    const itemIds = gear.map(g => g.item_id).filter(Boolean);
    const enchantIds = gear.map(g => g.enchant_id).filter(Boolean);
    const gemIds = gear.flatMap(g => g.gem_ids || []).filter(Boolean);
    if (itemIds.length === 0) return;

    resolveItems(itemIds, enchantIds, gemIds).then((itemRes) => {
      setResolved(itemRes);
      setTimeout(refreshWowheadTooltips, 500);
    }).catch(() => {});
  }, [data]);

  useEffect(() => { setTimeout(refreshWowheadTooltips, 300); }, [resolved, tab]);

  const gearMap = {};
  for (const g of gear) gearMap[g.slot] = g;
  const classColor = CLASS_COLORS[wowClass] || "#888";
  const hasStats = Object.keys(stats).length > 0;

  return (
    <div className="mt-4" data-testid="character-panel">
      <div className="flex gap-1 mb-4">
        <button onClick={() => setTab("gear")} data-testid="char-tab-gear"
          className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded-sm transition-colors ${tab === "gear" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
          <Eye className="h-3 w-3 inline mr-1" />Gear
        </button>
        {hasStats && (
          <button onClick={() => setTab("stats")} data-testid="char-tab-stats"
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded-sm transition-colors ${tab === "stats" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
            <Star className="h-3 w-3 inline mr-1" />Stats
          </button>
        )}
      </div>

      {tab === "gear" && (
        <div className="flex gap-4 justify-center" data-testid="paper-doll">
          <div className="flex flex-col gap-1.5 w-[200px]">
            {LEFT_SLOTS.map(slot => <SlotCard key={slot} slot={slot} gear={gearMap[slot]} resolved={resolved} />)}
          </div>
          <div className="flex flex-col items-center justify-center w-[120px] gap-3">
            {(() => {
              const items = gear.filter((g) => g.item_level > 0);
              const avg = items.length ? Math.round(items.reduce((s, g) => s + g.item_level, 0) / items.length) : 0;
              const c = ilvlColor(avg);
              return (
                <>
                  <div className="w-20 h-20 rounded-full flex items-center justify-center"
                       style={{ background: `${c.hex}22`, border: `2px solid ${c.hex}66` }}>
                    <Shield className="h-8 w-8" style={{ color: c.hex }} />
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Avg iLvl</p>
                    <p className="text-2xl font-bold font-mono" style={{ color: c.hex }}>
                      {avg || "—"}
                    </p>
                    <p className="text-[9px] font-mono uppercase tracking-wider" style={{ color: c.hex, opacity: 0.7 }}>
                      {c.label}
                    </p>
                  </div>
                </>
              );
            })()}
            <div className="text-center">
              <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Equipped</p>
              <p className="text-lg font-bold font-mono">{gear.filter(g => g.item_id > 0).length}/{gear.length}</p>
            </div>
          </div>
          <div className="flex flex-col gap-1.5 w-[200px]">
            {RIGHT_SLOTS.map(slot => <SlotCard key={slot} slot={slot} gear={gearMap[slot]} resolved={resolved} />)}
          </div>
        </div>
      )}

      {tab === "stats" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="stats-panel">
          <StatGroup title="Basic Stats" icon={Swords} stats={BASIC_STATS} values={stats} />
          <StatGroup title="Combat" icon={Zap} stats={COMBAT_STATS} values={stats} />
          <StatGroup title="Defense & Resistances" icon={Shield} stats={DEFENSE_STATS} values={stats} />
        </div>
      )}
    </div>
  );
}

function SlotCard({ slot, gear, resolved }) {
  const hasItem = gear?.item_id > 0;
  const info = hasItem ? resolved.items?.[String(gear.item_id)] : null;
  const itemColor = info?.color || "#333";
  const url = hasItem ? `${WOWHEAD_BASE}/item=${gear.item_id}${gear.enchant_id ? `&ench=${gear.enchant_id}` : ""}${gear.gem_ids?.length ? `&gems=${gear.gem_ids.join(":")}` : ""}` : "#";

  return (
    <a href={url} onClick={(e) => e.preventDefault()}
      data-testid={`paperdoll-${slot.toLowerCase().replace(/\s|\//g, "-")}`}
      className={`flex items-center gap-2 px-2 py-1.5 rounded-sm border transition-all hover:brightness-125 ${hasItem ? "border-border/30 bg-card/30 hover:bg-card/50" : "border-border/10 bg-card/10 opacity-40"}`}>
      {hasItem && info?.icon ? (
        <img src={info.icon} alt="" className="w-7 h-7 rounded-[3px] flex-shrink-0" style={{ borderWidth: 1, borderStyle: "solid", borderColor: itemColor }} loading="lazy" />
      ) : (
        <div className="w-7 h-7 rounded-[3px] bg-muted/20 border border-border/20 flex-shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground/60 leading-none">{slot}</p>
        {hasItem ? (
          <p className="text-xs truncate leading-tight" style={{ color: itemColor }}>{info?.name || `Item #${gear.item_id}`}</p>
        ) : (
          <p className="text-[10px] text-muted-foreground/30 leading-tight">Empty</p>
        )}
      </div>
      {hasItem && gear.item_level > 0 && (() => {
        const c = ilvlColor(gear.item_level);
        return (
          <span className="text-[10px] font-mono font-bold px-1 py-0.5 rounded-sm flex-shrink-0"
                style={{ color: c.hex, backgroundColor: `${c.hex}1a` }} title={c.label}>
            {gear.item_level}
          </span>
        );
      })()}
    </a>
  );
}

function StatGroup({ title, icon: Icon, stats, values }) {
  const nonZeroStats = stats.filter(s => (values[s.key] || 0) > 0);
  if (nonZeroStats.length === 0) return null;
  return (
    <div className="border border-border/20 rounded-sm overflow-hidden">
      <div className="px-3 py-2 bg-card/30 border-b border-border/10 flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{title}</span>
      </div>
      <div className="divide-y divide-border/10">
        {nonZeroStats.map(s => (
          <div key={s.key} className="px-3 py-1.5 flex items-center justify-between">
            <span className="text-sm" style={{ color: s.color }}>{s.label}</span>
            <span className="text-sm font-bold font-mono" style={{ color: s.color }}>{values[s.key]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
